import type {
  CreateUserBody,
  IssuedPassword,
  ListUsersQuery,
  Paginated,
  Role,
  UpdateUserBody,
  UserEntry,
} from '@obras/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, queryString } from '../../lib/api.js';

export interface OrgMember {
  readonly id: string;
  readonly name: string;
  readonly email: string | null;
  readonly role: Role;
  readonly jobTitle: string | null;
  readonly avatarUrl: string | null;
}

const usersKey = ['usuarios'] as const;

export function useUsers(query: Partial<ListUsersQuery>) {
  return useQuery({
    queryKey: [...usersKey, 'lista', query],
    queryFn: ({ signal }) =>
      api.get<Paginated<UserEntry>>(
        `/usuarios${queryString({
          page: query.page ?? 1,
          perPage: query.perPage ?? 50,
          search: query.search,
          role: query.role,
          status: query.status,
        })}`,
        signal,
      ),
    placeholderData: (previous) => previous,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateUserBody) => api.post<IssuedPassword>('/usuarios', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, body }: { userId: string; body: UpdateUserBody }) =>
      api.patch<{ ok: true }>(`/usuarios/${userId}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();

  // Redefinir tira de "aguardando senha" quem estava preso no fluxo do link;
  // sem recarregar, o selo continuaria afirmando o contrário na mesma tela.
  return useMutation({
    mutationFn: (userId: string) => api.post<IssuedPassword>(`/usuarios/${userId}/senha`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey }),
  });
}

/** Membros em formato enxuto, para os seletores de fiscal responsável. */
export function useOrgMembers(role?: Role) {
  return useQuery({
    queryKey: ['usuarios', 'opcoes', role ?? 'todos'],
    queryFn: ({ signal }) =>
      api.get<{ items: OrgMember[] }>(`/usuarios/opcoes${queryString({ role })}`, signal),
    staleTime: 5 * 60 * 1000,
  });
}
